import { mergeAnalyticsVisitor } from "./api";
import { getOrCreateAnonymousVisitorId, type IdentityStore } from "./identity";

// Client-side half of Épica 1 (platform build plan) -- pageview/click
// autocapture, batched and beaconed to POST /widget/events/batch. Deliberately
// its own module (clean separation, not a second network request -- see this
// file's own note in the build plan: splitting the single installed <script>
// into two would be a breaking install-snippet change for every existing
// customer for no real performance gain; the actual non-blocking win comes
// from recommending async/defer on that one script tag, not from this file
// being separate).

const FLUSH_INTERVAL_MS = 3_000;
// Well under the server's MAX_EVENTS_PER_BATCH (200) -- flushing more
// often, in smaller batches, keeps any single beacon payload small.
const FLUSH_MAX_EVENTS = 50;
const MAX_LABEL_LENGTH = 200;

export type DeviceType = "mobile" | "tablet" | "desktop";

export interface AnalyticsEventOut {
  agent_id: string;
  visitor_id: string;
  type: "pageview" | "click" | "custom" | "autofix_probe" | "backend_error";
  page_url: string;
  label: string | null;
  device_type: DeviceType;
  metadata: Record<string, unknown>;
}

export type AutofixProbeResult = "visible" | "covered" | "not_found";

/** Viewport width, not a user-agent string -- parsing UA strings reliably
 * needs a real library (a new dependency this widget deliberately has
 * none of, see widget/CLAUDE.md) and answers a question nobody here is
 * asking anyway: Épica 2's segment bar cares how the page actually
 * rendered for that visitor, which viewport width already answers
 * directly. Touch capability breaks the common "big phone vs small
 * tablet" tie that width alone can't. */
function detectDeviceType(): DeviceType {
  const width = window.innerWidth;
  const touch = navigator.maxTouchPoints > 0;
  if (width < 768) return "mobile";
  if (width < 1024 && touch) return "tablet";
  if (width < 1024) return "mobile";
  return "desktop";
}

/** A short, human-readable description of what was clicked -- good enough
 * for Épica 2's "pick from what Artis has seen" list. Deliberately NOT a
 * CSS selector: generating one that's both generic (works on any customer's
 * site, not just ones that tag everything with data-id) and precise is a
 * much harder problem, out of scope here (see the build plan's note on
 * this) -- a readable label is what that screen actually needs first. */
function describeClickTarget(target: EventTarget | null): string | null {
  if (!(target instanceof Element)) return null;
  const el = target.closest<Element>(
    "a, button, [role=button], [role=link], input, select, textarea, label, summary",
  );
  if (!el) return null;

  const tag = el.tagName.toLowerCase();
  const text = (el.textContent || "").trim().replace(/\s+/g, " ").slice(0, 80);
  const kind =
    tag === "a"
      ? "Link"
      : tag === "button" || el.getAttribute("role") === "button"
        ? "Button"
        : tag === "input"
          ? `Input (${(el as HTMLInputElement).type || "text"})`
          : tag;

  const label = text ? `${kind} "${text}"` : kind;
  return label.slice(0, MAX_LABEL_LENGTH);
}

export class AnalyticsModule {
  private readonly apiUrl: string;
  private readonly agentId: string;
  private readonly identity: IdentityStore;

  private consentGranted: boolean;
  private started = false;
  private queue: AnalyticsEventOut[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private lastVisitorId: string;
  private cleanupFns: Array<() => void> = [];

  constructor(opts: {
    apiUrl: string;
    agentId: string;
    identity: IdentityStore;
    waitForConsent: boolean;
  }) {
    this.apiUrl = opts.apiUrl;
    this.agentId = opts.agentId;
    this.identity = opts.identity;
    this.consentGranted = !opts.waitForConsent;
    this.lastVisitorId = this.resolveVisitorId();
  }

  /** The HMAC identity path (endUserId + userHash) exposes a real id
   * client-side directly; the JWT path's own end_user_id is decoded
   * server-side only (same caveat index.ts documents for the first-visit
   * trigger) -- resolvedVisitorId is how that path's real id reaches
   * here too, once the server hands it back (conversation creation or
   * identify(), see identity.ts's setResolvedVisitorId). Falls back to
   * the stable anonymous id, same one public_identity conversations
   * already use (not a second, parallel id) -- that id is now always
   * created when analytics is on, regardless of whether this agent's
   * public_identity is set. */
  private resolveVisitorId(): string {
    const identity = this.identity.get();
    return identity?.resolvedVisitorId || identity?.endUserId || getOrCreateAnonymousVisitorId();
  }

  start(): void {
    if (this.started) return;
    this.started = true;

    this.identity.onChange(() => {
      const next = this.resolveVisitorId();
      if (next !== this.lastVisitorId && this.lastVisitorId.startsWith("anon-")) {
        mergeAnalyticsVisitor(this.apiUrl, this.agentId, this.lastVisitorId, next);
      }
      this.lastVisitorId = next;
    });

    this.capturePageview();
    this.armSpaPageviewDetection();
    this.armClickCapture();
    this.armFlushTriggers();
  }

  /** Task D4/D4a -- one `autofix_probe` event per visitor per fix
   * (autofixProbe.ts dedups the "per visitor per fix" part itself, via
   * localStorage; this is just the send). `fix_id` is the only
   * identifying detail in metadata -- same minimal-payload discipline
   * every other event here already follows (see AnalyticsEventIn's own
   * docstring on ai-service). */
  captureAutofixProbe(fixId: string, result: AutofixProbeResult, viewportWidth: number): void {
    this.enqueue({
      type: "autofix_probe",
      page_url: location.pathname,
      label: null,
      metadata: { fix_id: fixId, result, viewport_width: viewportWidth },
    });
  }

  /** Task G1 -- one `backend_error` event per (status_code, endpoint_path,
   * error_code) per page load (backendErrorCapture.ts dedups that part
   * itself, in-memory; this is just the send). Same minimal, flat-metadata
   * payload discipline as captureAutofixProbe -- flat on purpose, so
   * ai-service's existing shallow redact_metadata already covers it. */
  captureBackendError(
    statusCode: number,
    endpointPath: string,
    errorCode: string | null,
    errorMessage: string | null,
  ): void {
    this.enqueue({
      type: "backend_error",
      page_url: location.pathname,
      label: null,
      metadata: {
        status_code: statusCode,
        endpoint_path: endpointPath,
        error_code: errorCode,
        error_message: errorMessage,
      },
    });
  }

  /** Lets the host page gate capture behind its own cookie-consent flow
   * (analytics_wait_for_consent) -- queues nothing and sends nothing until
   * called, when that flag is set. */
  grantConsent(): void {
    if (this.consentGranted) return;
    this.consentGranted = true;
    if (this.started) this.flush();
  }

  private enqueue(
    event: Omit<AnalyticsEventOut, "agent_id" | "visitor_id" | "device_type">,
  ): void {
    if (!this.consentGranted) return;
    this.queue.push({
      agent_id: this.agentId,
      visitor_id: this.lastVisitorId,
      device_type: detectDeviceType(),
      ...event,
    });
    if (this.queue.length >= FLUSH_MAX_EVENTS) this.flush();
  }

  /** The identity's own optional metadata (today just `company`, see
   * identity.ts's EndUserMetadata) -- real traits the host page already
   * passed to identify()/identifyWithToken(), not anything this widget
   * infers. Lets the dashboard eventually segment/filter analytics by
   * them; nothing reads this yet, but the field already exists end to
   * end (AnalyticsEvent.metadata_) and was previously always sent empty. */
  private identityMetadata(): Record<string, unknown> {
    return { ...(this.identity.get()?.metadata ?? {}) };
  }

  private capturePageview(): void {
    this.enqueue({ type: "pageview", page_url: location.pathname, label: null, metadata: this.identityMetadata() });
  }

  /** Single-page apps change the visible "page" without a real navigation
   * -- both the History API (pushState/replaceState, used by every
   * client-side router) and popstate (back/forward) need to be watched so
   * a pageview fires on each route change, not just on the first load. */
  private armSpaPageviewDetection(): void {
    let lastPath = location.pathname;
    const onRouteChange = () => {
      if (location.pathname === lastPath) return;
      lastPath = location.pathname;
      this.capturePageview();
    };

    const originalPushState = history.pushState.bind(history);
    const originalReplaceState = history.replaceState.bind(history);
    history.pushState = (...args: Parameters<History["pushState"]>) => {
      originalPushState(...args);
      onRouteChange();
    };
    history.replaceState = (...args: Parameters<History["replaceState"]>) => {
      originalReplaceState(...args);
      onRouteChange();
    };
    window.addEventListener("popstate", onRouteChange);

    this.cleanupFns.push(() => {
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", onRouteChange);
    });
  }

  private armClickCapture(): void {
    const onClick = (event: MouseEvent) => {
      const label = describeClickTarget(event.target);
      if (!label) return;
      this.enqueue({ type: "click", page_url: location.pathname, label, metadata: this.identityMetadata() });
    };
    // Capture phase, same idiom as index.ts's existing rage-click listener
    // -- sees every click regardless of what a page's own handlers do with
    // it (stopPropagation, preventDefault, etc).
    document.addEventListener("click", onClick, { capture: true });
    this.cleanupFns.push(() =>
      document.removeEventListener("click", onClick, { capture: true }),
    );
  }

  private armFlushTriggers(): void {
    this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);

    // pagehide (not just beforeunload, which is unreliable on mobile/bfcache)
    // is the one moment a beacon-style send actually has to happen
    // synchronously -- anything queued after the periodic timer's last tick
    // would otherwise be silently lost when the tab closes.
    const onPageHide = () => this.flush();
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") this.flush();
    });
    this.cleanupFns.push(() => window.removeEventListener("pagehide", onPageHide));
  }

  private flush(): void {
    if (!this.consentGranted || this.queue.length === 0) return;
    const events = this.queue;
    this.queue = [];
    const body = JSON.stringify({ events });

    // sendBeacon survives the page unloading; it's the right tool for this
    // and is what pagehide/visibilitychange above exist to trigger. Fall
    // back to a keepalive fetch for the (rare, and non-unload) case it's
    // unavailable or refuses the payload.
    const url = `${this.apiUrl}/ai-service/api/v1/widget/events/batch`;
    const sent =
      typeof navigator.sendBeacon === "function" &&
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    if (!sent) {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  }

  stop(): void {
    if (this.flushTimer) clearInterval(this.flushTimer);
    this.flushTimer = null;
    for (const cleanup of this.cleanupFns) cleanup();
    this.cleanupFns = [];
    this.started = false;
  }
}
